/**
 * PromotionCart — review + checkout for queued promotion stacks.
 *
 * Backend: getPromotionCart / checkoutPromotionCart / removePromotionFromCart.
 * The server returns:
 *   { items, grandTotal, tokenBalance, shortfall, canCheckout }
 * where each item has { id, targetType, targetId, targetName, promotionType,
 * count, startAt, endAt, tokenCost }.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoins,
  faShoppingCart,
  faTimesCircle,
  faCheckCircle,
  faExclamationTriangle,
  faArrowRight,
  faStore,
  faCalendarDay,
} from "@fortawesome/free-solid-svg-icons";
import { PromotionCartApi } from "../services/PromotionCartApi";
import { PROMOTION_TIERS } from "../constants/promotionTiers";
import { detectBrowserTimezone, formatInUserTz } from "../utils/datetime";
import { useAuth } from "../contexts/CombinedAuthContext";
import TokenSpendIndicator from "../components/TokenSpendIndicator";
import "../styles/PromotionCart.css";

export default function PromotionCart() {
  const navigate = useNavigate();
  const tz = detectBrowserTimezone();
  const { firebaseUser } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await PromotionCartApi.get();
      setCart(data);
    } catch (err) {
      setError(err?.message || "Failed to load cart.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleRemove = useCallback(async (itemId) => {
    try {
      await PromotionCartApi.remove(itemId);
      await refresh();
    } catch (err) {
      setError(err?.message || "Failed to remove item.");
    }
  }, [refresh]);

  const handleCheckout = useCallback(async () => {
    if (!cart?.canCheckout) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await PromotionCartApi.checkout();
      setSuccessMsg(
        `Checkout complete — ${res.grandTotal} Doubloons spent, ${res.newTokenBalance} remaining.`,
      );
      setTimeout(() => navigate("/business-dashboard"), 1800);
    } catch (err) {
      setError(err?.message || "Checkout failed.");
    } finally {
      setSubmitting(false);
    }
  }, [cart, navigate]);

  const grouped = useMemo(() => groupByTarget(cart?.items || []), [cart]);

  if (loading) {
    return (
      <div className="promo-cart">
        <h1 className="promo-cart__title"><FontAwesomeIcon icon={faShoppingCart} /> Promotion cart</h1>
        <p className="promo-cart__empty">Loading…</p>
      </div>
    );
  }

  const items = cart?.items || [];
  const empty = items.length === 0;

  return (
    <div className="promo-cart">
      <header className="promo-cart__header">
        <div className="promo-cart__header-text">
          <h1 className="promo-cart__title">
            <FontAwesomeIcon icon={faShoppingCart} /> Promotion cart
          </h1>
          <p className="promo-cart__subtitle">
            Review your queued promotion stacks. Tokens are only spent on checkout.
          </p>
        </div>
        <TokenSpendIndicator
          userId={firebaseUser?.uid}
          src="promotion-cart"
          className="promo-cart__token-indicator"
        />
      </header>

      {error && (
        <div className="promo-cart__alert promo-cart__alert--error" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}
      {successMsg && (
        <div className="promo-cart__alert promo-cart__alert--success">
          <FontAwesomeIcon icon={faCheckCircle} /> {successMsg}
        </div>
      )}

      {empty ? (
        <div className="promo-cart__empty-state">
          <p>Your cart is empty.</p>
          <Link to="/business-dashboard" className="promo-cart__cta">
            Go to your dashboard <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      ) : (
        <>
          <div className="promo-cart__groups">
            {grouped.map((group) => (
              <div key={`${group.targetType}:${group.targetId}`} className="promo-cart__group">
                <h3 className="promo-cart__group-title">
                  <FontAwesomeIcon icon={group.targetType === "event" ? faCalendarDay : faStore} />
                  {" "}
                  {group.targetName || group.targetId}
                </h3>
                <ul className="promo-cart__items">
                  {group.items.map((item) => (
                    <li key={item.id} className="promo-cart__item">
                      <div className="promo-cart__item-body">
                        <span className="promo-cart__item-title">
                          {item.promotionType === "custom"
                            ? "Custom range"
                            : (PROMOTION_TIERS[item.promotionType]?.label || item.promotionType)}
                          {item.count > 1 ? ` × ${item.count}` : ""}
                        </span>
                        <span className="promo-cart__item-range">
                          {formatTs(item.startAt, tz)} → {formatTs(item.endAt, tz)}
                        </span>
                      </div>
                      <span className="promo-cart__item-cost">
                        {item.tokenCost} <FontAwesomeIcon icon={faCoins} />
                      </span>
                      <button
                        type="button"
                        className="promo-cart__remove"
                        onClick={() => handleRemove(item.id)}
                        aria-label="Remove item"
                      >
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="promo-cart__summary">
            <div className="promo-cart__summary-row">
              <span>Grand total</span>
              <strong>{cart.grandTotal} Doubloons</strong>
            </div>
            {cart.shortfall > 0 && (
              <div className="promo-cart__shortfall">
                You need <strong>{cart.shortfall}</strong> more Doubloons.{" "}
                <Link to={`/tokens?want=${cart.shortfall}&src=promotion-cart&intent=topup`}>Buy Doubloons</Link>
              </div>
            )}
            <button
              type="button"
              className="promo-cart__checkout"
              onClick={handleCheckout}
              disabled={!cart.canCheckout || submitting}
            >
              {submitting ? "Processing…" : `Check out (${cart.grandTotal} Doubloons)`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function groupByTarget(items) {
  const m = new Map();
  for (const item of items) {
    const key = `${item.targetType}:${item.targetId}`;
    if (!m.has(key)) {
      m.set(key, {
        targetType: item.targetType,
        targetId: item.targetId,
        targetName: item.targetName,
        items: [],
      });
    }
    m.get(key).items.push(item);
  }
  return Array.from(m.values());
}

function formatTs(ts, tz) {
  const ms = toMs(ts);
  if (!ms) return "—";
  return formatInUserTz(new Date(ms), tz, { dateStyle: "medium", timeStyle: "short" });
}

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts === "string" || typeof ts === "number") {
    const d = new Date(ts).getTime();
    return Number.isFinite(d) ? d : 0;
  }
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts._seconds) return ts._seconds * 1000;
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  return 0;
}
